import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap,
  PiggyBank,
  Target,
  Flame,
  MapPin,
  Trophy,
  Moon,
  Globe,
  Fuel,
  Star,
  Users,
  Activity,
  DollarSign,
} from 'lucide-react';
import { useConfettiStore } from '../../../stores/confetti-store';

// ── Icon + color lookup (must mirror BadgesPage mappings) ────────────
const BADGE_ICONS: Record<string, typeof Zap> = {
  zap: Zap,
  'piggy-bank': PiggyBank,
  target: Target,
  flame: Flame,
  'map-pin': MapPin,
  moon: Moon,
  globe: Globe,
  fuel: Fuel,
  star: Star,
  users: Users,
  activity: Activity,
  'dollar-sign': DollarSign,
};

const BADGE_COLORS: Record<string, { bg: string; border: string; text: string; glow: string }> = {
  zap: { bg: '#FFF7E6', border: '#F5A623', text: '#B8860B', glow: '#F5A623' },
  'piggy-bank': { bg: '#E8F5E9', border: '#2DC47A', text: '#1B7A3D', glow: '#2DC47A' },
  target: { bg: '#E3F2FD', border: '#468AD4', text: '#1B3050', glow: '#468AD4' },
  flame: { bg: '#FBE9E7', border: '#ed5e5e', text: '#B71C1C', glow: '#ed5e5e' },
  'map-pin': { bg: '#F3E5F5', border: '#865ce2', text: '#4A148C', glow: '#865ce2' },
  moon: { bg: '#EDE7F6', border: '#7C4DFF', text: '#311B92', glow: '#7C4DFF' },
  globe: { bg: '#E0F2F1', border: '#009688', text: '#004D40', glow: '#009688' },
  fuel: { bg: '#FFF3E0', border: '#FF9800', text: '#E65100', glow: '#FF9800' },
  star: { bg: '#FFFDE7', border: '#FDD835', text: '#F57F17', glow: '#FDD835' },
  users: { bg: '#E8EAF6', border: '#5C6BC0', text: '#1A237E', glow: '#5C6BC0' },
  activity: { bg: '#FCE4EC', border: '#E91E63', text: '#880E4F', glow: '#E91E63' },
  'dollar-sign': { bg: '#E8F5E9', border: '#4CAF50', text: '#1B5E20', glow: '#4CAF50' },
};

// ── Confetti particle generator ──────────────────────────────────────
const CONFETTI_COLORS = [
  '#F5A623', '#2DC47A', '#468AD4', '#865ce2', '#ed5e5e',
  '#e8bc52', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
  '#FFEAA7', '#DDA0DD', '#FF9FF3', '#54A0FF', '#5F27CD',
];

interface Particle {
  id: number;
  x: number;
  delay: number;
  duration: number;
  color: string;
  size: number;
  rotation: number;
  wobble: number;
  shape: 'circle' | 'square' | 'strip';
}

function generateParticles(count: number): Particle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 1.2,
    duration: 2 + Math.random() * 2,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    size: 6 + Math.random() * 8,
    rotation: Math.random() * 360,
    wobble: (Math.random() - 0.5) * 200,
    shape: (['circle', 'square', 'strip'] as const)[Math.floor(Math.random() * 3)],
  }));
}

// ── Main Component ───────────────────────────────────────────────────
export function ConfettiCelebration() {
  const { t } = useTranslation();
  const { isActive, badge, dismiss } = useConfettiStore();
  const particles = useMemo(() => generateParticles(80), []);

  // Dismiss on Escape key
  useEffect(() => {
    if (!isActive) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isActive, dismiss]);

  const Icon = badge ? (BADGE_ICONS[badge.icon] || Trophy) : Trophy;
  const colors = badge ? (BADGE_COLORS[badge.icon] || BADGE_COLORS.zap) : BADGE_COLORS.zap;

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={dismiss}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            cursor: 'pointer',
            overflow: 'hidden',
          }}
        >
          {/* Confetti particles falling from top */}
          {particles.map((p) => {
            const shapeStyle: React.CSSProperties =
              p.shape === 'circle'
                ? { borderRadius: '50%', width: p.size, height: p.size }
                : p.shape === 'strip'
                  ? { borderRadius: '2px', width: p.size * 0.4, height: p.size * 1.5 }
                  : { borderRadius: '2px', width: p.size, height: p.size };

            return (
              <motion.div
                key={p.id}
                initial={{
                  x: `${p.x}vw`,
                  y: '-5vh',
                  rotate: 0,
                  opacity: 1,
                }}
                animate={{
                  x: `calc(${p.x}vw + ${p.wobble}px)`,
                  y: '110vh',
                  rotate: p.rotation + 720,
                  opacity: [1, 1, 0.8, 0],
                }}
                transition={{
                  duration: p.duration,
                  delay: p.delay,
                  ease: 'easeIn',
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  backgroundColor: p.color,
                  pointerEvents: 'none',
                  ...shapeStyle,
                }}
              />
            );
          })}

          {/* Badge celebration card */}
          {badge && (
            <motion.div
              initial={{ scale: 0, rotate: -15, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 15,
                delay: 0.3,
              }}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                padding: '40px 48px',
                borderRadius: '24px',
                background: '#fff',
                boxShadow: `0 0 80px ${colors.glow}60, 0 20px 60px rgba(0,0,0,0.15)`,
                textAlign: 'center',
                zIndex: 10,
              }}
            >
              {/* Glow ring behind icon */}
              <motion.div
                animate={{
                  boxShadow: [
                    `0 0 20px ${colors.glow}40`,
                    `0 0 50px ${colors.glow}60`,
                    `0 0 20px ${colors.glow}40`,
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  width: '88px',
                  height: '88px',
                  borderRadius: '50%',
                  backgroundColor: colors.bg,
                  border: `3px solid ${colors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon
                  style={{
                    width: '40px',
                    height: '40px',
                    color: colors.text,
                  }}
                />
              </motion.div>

              <div>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '1.5px',
                    color: colors.text,
                    marginBottom: '4px',
                  }}
                >
                  {t('components.confettiCelebration.badgeEarned')}
                </motion.p>
                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  style={{
                    fontSize: '22px',
                    fontWeight: 700,
                    color: '#212529',
                    margin: '0 0 6px 0',
                  }}
                >
                  {badge.name}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  style={{
                    fontSize: '14px',
                    color: '#6c757d',
                    maxWidth: '280px',
                    lineHeight: 1.5,
                  }}
                >
                  {badge.description}
                </motion.p>
              </div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                transition={{ delay: 1.5 }}
                style={{
                  fontSize: '11px',
                  color: '#adb5bd',
                  marginTop: '4px',
                }}
              >
                {t('components.confettiCelebration.clickToDismiss')}
              </motion.p>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
